import Clickable from 'components/Common/Clickable'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import Label from 'LuxKit/Label'
import NeutralLabel from 'LuxKit/Label/NeutralLabel'
import UltraLuxLogo from 'LuxKit/Label/UltraLuxLogo'
import useTourStyledLabels from 'hooks/Tours/useTourStyledLabels'
import ModalContext from 'contexts/ModalContext'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import TourStyledProductTypeInfoModal from './TourStyledProductTypeInfoModal'
import { isTourProductTypeStyled } from 'lib/tours/tourUtils'
import { LUXURY_ESCAPES } from 'constants/brands'
import config from 'constants/config'
import useProductLabels from 'hooks/useProductLabels'
import SolidBrightnessEmptyIcon from 'LuxKit/Icons/solid/SolidBrightnessEmptyIcon'

interface Props {
  productType: App.Tours.V2ProductType
  offerUrl?: string
  offerId?: string
}

function TourProductTypeLabel({ productType, offerUrl, offerId }: Props) {
  const showModal = useContext(ModalContext)
  const intl = useIntl()
  const { styledProductTypeTitles } = useTourStyledLabels()
  const { lePartner } = useProductLabels()

  const viewOfferText = intl.formatMessage(
    {
      id: 'J52ed7bVh35ZdIauoYe-',
      defaultMessage: 'View offer',
    },
  )

  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    if (isTourProductTypeStyled(productType)) {
      showModal(<TourStyledProductTypeInfoModal
        offerId={offerId}
        productType={productType}
        primaryActionText={offerUrl ? viewOfferText : undefined}
        primaryActionTo={offerUrl}
      />)
    }
  }, [showModal, productType, offerId, offerUrl, viewOfferText])

  const neutralLabelProps = useMemo<Partial<Record<App.Tours.V2ProductTypeStyled, ComponentProps<typeof NeutralLabel>>>>(() => ({
    deluxe_tour: {
      children: styledProductTypeTitles.deluxe_tour,
    },
    premium_tour: {
      startIcon: <SolidBrightnessEmptyIcon />,
      children: styledProductTypeTitles.premium_tour,
    },
    signature_series_tour: {
      startIcon: <SolidStarCircleIcon />,
      children: styledProductTypeTitles.signature_series_tour,
    },
  }), [styledProductTypeTitles])

  const labelProps = useMemo<Record<App.Tours.V2ProductType, ComponentProps<typeof Label> | undefined>>(() => ({
    deluxe_tour: undefined,
    premium_tour: undefined,
    ultra_lux_tour: {
      kind: 'secondary',
      variant: 'ghost',
      children: <UltraLuxLogo height={10} />,
    },
    signature_series_tour: undefined,
    partner_tour: config.BRAND === LUXURY_ESCAPES ? {
      kind: 'secondary',
      variant: 'grey',
      children: lePartner,
    } : undefined,
    custom_tour: undefined,
    direct_tour: undefined,
    connection_tour: undefined,
    cruise_tour: undefined,
    insider_series_tour: undefined,
  }), [lePartner])

  const neutralConfigProps = neutralLabelProps[productType as App.Tours.V2ProductTypeStyled]
  const configProps = labelProps[productType]

  if (!neutralConfigProps && !configProps) return null

  return <Clickable onClick={handleClick}>
    {neutralConfigProps ? <NeutralLabel {...neutralConfigProps} /> : <Label {...configProps!} />}
  </Clickable>
}

export default TourProductTypeLabel
